const DEFAULT_MIN = 100;
const DEFAULT_GROW = 1;

const defaultConfig = {
  min: DEFAULT_MIN,
  grow: DEFAULT_GROW
};

type ColumnConfig = {
  min?: number;
  grow?: number;
};

type RColumnConfig = Required<ColumnConfig> & { index: number };

function mergeDefaults(columnConfig: ColumnConfig[]): RColumnConfig[] {
  return columnConfig.map((x, i) => ({
    ...defaultConfig,
    ...x,
    index: i
  }));
}

function getTotalOfKey(key: keyof RColumnConfig) {
  return (config: RColumnConfig[]) => {
    return config.reduce((red, cur) => red + cur[key], 0);
  };
}

const calcTotalMinWidth = getTotalOfKey("min");
const calcTotalGrow = getTotalOfKey("grow");

function calcGrowWidth(totalGrow: number, totalWidth: number, grow: number) {
  return (grow / totalGrow) * totalWidth;
}

function indexOfLackingColumn(
  totalGrow: number,
  totalWidth: number,
  columnConfig: RColumnConfig[]
) {
  return columnConfig.findIndex(config => {
    const grownWidth = calcGrowWidth(totalGrow, totalWidth, config.grow);
    return grownWidth < config.min;
  });
}

function divideRemainingSpace(
  totalWidth: number,
  columnConfig: RColumnConfig[]
): number[] {
  // set configs with no grow aside
  let result = columnConfig
    .filter(x => x.grow === 0)
    .map(x => ({ index: x.index, width: x.min }));

  // set configs with grow aside
  const grows = columnConfig.filter(x => x.grow > 0);

  let leftRemaining = totalWidth;

  while (result.length !== columnConfig.length) {
    const totalGrow = calcTotalGrow(grows);

    const index = indexOfLackingColumn(totalGrow, leftRemaining, grows);
    // in case grown width of column does not reach the specified min-width of column
    // hardcode width to the specified min-width and move config to result set, and
    // adjust remaining space accordingly. Repeat this process until all min-widths of
    // the columns are satisfied
    if (index > -1) {
      // remove config from stack
      const config = grows.splice(index, 1);

      // add to result set
      result.push({
        index: config[0].index,
        width: config[0].min
      });

      // adjust remaining space;
      leftRemaining = leftRemaining - config[0].min;
      continue;
    }

    result = result.concat(
      grows.map(config => ({
        index: config.index,
        width: calcGrowWidth(totalGrow, leftRemaining, config.grow)
      }))
    );
  }

  // sortBy index and return widths[]
  return result.sort((a, b) => a.index - b.index).map(x => x.width);
}

function getColumnWidths(
  totalWidth: number,
  columnConfig: ColumnConfig[]
): number[] {
  const withDefaults = mergeDefaults(columnConfig);

  const totalMinWidth = calcTotalMinWidth(withDefaults);

  if (totalMinWidth >= totalWidth) {
    // we can stop right here
    // just return min-widths[]
    return withDefaults.map(x => x.min);
  }

  return divideRemainingSpace(totalWidth, withDefaults);
}

export default getColumnWidths;
